import { useCallback, useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { mapAdminError } from '@/components/Admin/messages';
import { ambienteLabel } from '@/components/Admin/ambiente_labels';
import { TrilhaTabela, type ColunaTrilha } from '@/components/Trilha/TrilhaTabela';
import { instanteDoDiaLocal } from '@/lib/trilha_format';
import {
  AdminError,
  exportarTrilha,
  listTrilha,
  type AmbienteAdmin,
  type FiltroTrilha,
  type FonteTrilha,
  type TrilhaEvento,
} from '@/lib/plataforma_admin';
import { Button, Input, Select } from '@/components/ui';

const _POR_PAGINA = 25;

// O console vê os dois eixos que só ele tem: de qual ambiente veio o autor e de
// qual origem veio o registro. A tela do banco não mostra nenhum dos dois.
const _COLUNAS_DO_CONSOLE: readonly ColunaTrilha[] = [
  'data',
  'acao',
  'autor',
  'ambiente',
  'objeto',
  'origem',
];

function _mensagem(err: unknown): string {
  return err instanceof AdminError ? mapAdminError(err.reason) : ptBR.admin.erroGenerico;
}

/**
 * Console `/admin/trilha` — "o que aconteceu com este cliente?" (E53.F7). // ux-neutrality:allow doc-comment
 *
 * O export usa o MESMO recorte da consulta: um arquivo que não bate com a tela // ux-neutrality:allow doc-comment
 * é pior que nenhum arquivo. Quando o recorte é grande demais, o servidor recusa // ux-neutrality:allow doc-comment
 * e a tela pede um período menor — nada é truncado em silêncio. // ux-neutrality:allow doc-comment
 */
export function AdminTrilhaPage() {
  const apiClient = useApiClient();
  const [eventos, setEventos] = useState<TrilhaEvento[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');
  const [autor, setAutor] = useState('');
  const [ambiente, setAmbiente] = useState<AmbienteAdmin | ''>('');
  const [fonte, setFonte] = useState<FonteTrilha | ''>('');
  const [caso, setCaso] = useState('');
  const pedidoRef = useRef(0);

  const recorte = useCallback(
    (comJanela: boolean): FiltroTrilha => ({
      // A data do filtro é civil; o servidor compara instantes. A conversão
      // carrega o fuso do operador — é o mesmo com que a tabela mostra a hora
      // de cada evento.
      ...(desde === '' ? {} : { desde: instanteDoDiaLocal(desde, 'inicio') }),
      ...(ate === '' ? {} : { ate: instanteDoDiaLocal(ate, 'fim') }),
      ...(autor.trim() === '' ? {} : { autor: autor.trim() }),
      ...(ambiente === '' ? {} : { ambiente }),
      ...(fonte === '' ? {} : { fonte }),
      ...(caso.trim() === '' ? {} : { caso: caso.trim() }),
      ...(comJanela ? { limite: _POR_PAGINA, offset: pagina * _POR_PAGINA } : {}),
    }),
    [ambiente, ate, autor, caso, desde, fonte, pagina],
  );

  const carregar = useCallback(async () => {
    const pedido = pedidoRef.current + 1;
    pedidoRef.current = pedido;
    try {
      const resposta = await listTrilha(apiClient, recorte(true));
      if (pedido !== pedidoRef.current) return;
      setEventos(resposta.itens);
      setTotal(resposta.total);
      setErroLista(null);
    } catch (err) {
      if (pedido !== pedidoRef.current) return;
      setEventos([]);
      setErroLista(_mensagem(err));
    }
  }, [apiClient, recorte]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function exportar() {
    setExportando(true);
    setErroAcao(null);
    setSucesso(null);
    try {
      const blob = await exportarTrilha(apiClient, recorte(false));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'trilha-auditoria.csv';
      link.click();
      URL.revokeObjectURL(url);
      setSucesso(ptBR.admin.trilhaExportSucesso);
    } catch (err) {
      setErroAcao(_mensagem(err));
    } finally {
      setExportando(false);
    }
  }

  function trocarFiltro(acao: () => void) {
    acao();
    setPagina(0);
  }

  const inicio = total === 0 ? 0 : pagina * _POR_PAGINA + 1;
  const fim = Math.min(total, (pagina + 1) * _POR_PAGINA);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-5 p-6">
      <header>
        <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">
          {ptBR.admin.trilhaEyebrow}
        </p>
        <h1 className="text-display-md text-fg">{ptBR.admin.trilhaTitle}</h1>
        <p className="mt-1 text-body-md text-fg-muted">{ptBR.admin.trilhaIntro}</p>
      </header>

      {sucesso !== null ? (
        <p role="status" data-testid="admin-sucesso" className="text-body-sm text-accent-700">
          {sucesso}
        </p>
      ) : null}
      {erroAcao !== null ? (
        <p role="alert" data-testid="admin-erro-acao" className="text-body-sm text-danger-700">
          {erroAcao}
        </p>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.trilhaDesde}</span>
          <Input
            type="date"
            value={desde}
            onChange={(evento) => trocarFiltro(() => setDesde(evento.target.value))}
            data-testid="trilha-desde"
          />
        </label>
        <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.trilhaAte}</span>
          <Input
            type="date"
            value={ate}
            onChange={(evento) => trocarFiltro(() => setAte(evento.target.value))}
            data-testid="trilha-ate"
          />
        </label>
        <label className="flex min-w-[180px] flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.trilhaAutorLabel}</span>
          <Input
            value={autor}
            onChange={(evento) => trocarFiltro(() => setAutor(evento.target.value))}
            data-testid="trilha-autor"
          />
        </label>
        <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.ambienteFiltroLabel}</span>
          <Select
            value={ambiente}
            onChange={(evento) =>
              trocarFiltro(() => setAmbiente(evento.target.value as AmbienteAdmin | ''))
            }
            data-testid="trilha-ambiente"
          >
            <option value="">{ptBR.admin.ambienteTodos}</option>
            <option value="plataforma">{ptBR.admin.ambientePlataforma}</option>
            <option value="escritorio">{ptBR.admin.ambienteEscritorio}</option>
            <option value="banco">{ptBR.admin.ambienteBanco}</option>
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.trilhaOrigemLabel}</span>
          <Select
            value={fonte}
            onChange={(evento) =>
              trocarFiltro(() => setFonte(evento.target.value as FonteTrilha | ''))
            }
            data-testid="trilha-fonte"
          >
            <option value="">{ptBR.admin.trilhaOrigemTodas}</option>
            <option value="administracao">{ptBR.trilha.origemAdministracao}</option>
            <option value="assinatura">{ptBR.trilha.origemAssinatura}</option>
            <option value="medicao">{ptBR.trilha.origemMedicao}</option>
            <option value="requisicao">{ptBR.trilha.origemRequisicao}</option>
            <option value="edicao_laudo">{ptBR.trilha.origemEdicaoLaudo}</option>
            <option value="compartilhamento_laudo">{ptBR.trilha.origemCompartilhamento}</option>
            <option value="caso">{ptBR.trilha.origemCaso}</option>
          </Select>
        </label>
        <label className="flex min-w-[140px] flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.trilha.filtroCaso}</span>
          <Input
            value={caso}
            placeholder={ptBR.trilha.filtroCasoPlaceholder}
            onChange={(evento) => trocarFiltro(() => setCaso(evento.target.value))}
            data-testid="trilha-caso"
          />
        </label>
        <Button
          variant="secondary"
          type="button"
          disabled={exportando}
          onClick={() => void exportar()}
          data-testid="trilha-exportar"
        >
          {exportando ? ptBR.admin.trilhaExportando : ptBR.admin.trilhaExportar}
        </Button>
      </div>

      <TrilhaTabela
        eventos={eventos}
        erro={erroLista}
        colunas={_COLUNAS_DO_CONSOLE}
        ambienteLabel={ambienteLabel}
      />

      <div className="flex items-center gap-3 text-body-sm text-fg-muted">
        <Button
          variant="secondary"
          type="button"
          disabled={pagina === 0}
          onClick={() => setPagina((atual) => Math.max(0, atual - 1))}
          data-testid="trilha-anterior"
        >
          {ptBR.admin.paginaAnterior}
        </Button>
        <Button
          variant="secondary"
          type="button"
          disabled={fim >= total}
          onClick={() => setPagina((atual) => atual + 1)}
          data-testid="trilha-proxima"
        >
          {ptBR.admin.paginaProxima}
        </Button>
        <span data-testid="trilha-resumo">
          {ptBR.admin.paginaResumo
            .replace('{inicio}', String(inicio))
            .replace('{fim}', String(fim))
            .replace('{total}', String(total))}
        </span>
      </div>
    </section>
  );
}

export default AdminTrilhaPage;
